import {
  useQuery, useLazyQuery, DocumentNode, TypedDocumentNode, QueryHookOptions,
  QueryResult, OperationVariables, LazyQueryHookOptions, LazyQueryResultTuple, ApolloError,
} from '@apollo/client';
import { useCallback } from 'react';
import { useGlobalState } from '../../custom_hooks/useGlobalState';
import { APP_STATES, AppState } from '../../lib/global';

export const filterGraphQlData = (data) => Object.keys(data).filter((k) => k !== '__typename').reduce((acc, k) => {
  acc[k] = data[k];
  return acc;
}, {});

export function useQueryCached<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> {
  const [appState] = useGlobalState(AppState);
  const shouldPause = appState === APP_STATES.maintenance
    || appState === APP_STATES.down;

  const result = useQuery<TData, TVariables>(query, {
    ...options,
    skip: shouldPause || options?.skip,
    fetchPolicy: appState === APP_STATES.restoration
      ? 'cache-only'
      : options?.fetchPolicy,
  });

  const isActuallyLoading = result.loading
    && !result.client?.readQuery({ query, variables: options?.variables });

  return { ...result, loading: isActuallyLoading };
}

export function useLazyQueryCached<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: LazyQueryHookOptions<TData, TVariables>,
): LazyQueryResultTuple<TData, TVariables> {
  const [appState] = useGlobalState(AppState);
  const shouldPause = appState === APP_STATES.maintenance
    || appState === APP_STATES.down;

  const [executeFunction, values] = useLazyQuery<TData, TVariables>(query, {
    ...options,
    ...(appState === APP_STATES.restoration && { fetchPolicy: 'cache-only' }),
  });

  const guardedExecute: typeof executeFunction = useCallback(
    (execOptions) => {
      if (shouldPause) {
        // Restituisce una Promise che non si risolve mai, senza lanciare errori
        return new Promise(() => {}) as ReturnType<typeof executeFunction>;
      }
      return executeFunction(execOptions);
    },
    [shouldPause, executeFunction],
  );

  const isActuallyLoading = values.loading
    && !values.client?.readQuery({ query, variables: options?.variables });

  return [guardedExecute, { ...values, loading: isActuallyLoading }];
}

/**
 * Gets, if there is, a "404" `message" in the Apollo response
 *
 * We use `graphQLErrors.message` for response statuses in 404 situations
 *
 * @param graphQLErrors
 */
export const get404Responses = (graphQLErrors: ApolloError) => (graphQLErrors.graphQLErrors || graphQLErrors)
  .filter((ge) => ge.message === '404').length > 0;
